/**
 * PositionLevelFormModal 컴포넌트
 *
 * 직위(Position Level) 생성/수정 모달 폼입니다.
 *
 * **모드:**
 * - 생성 모드: positionLevelId가 undefined
 * - 수정 모드: positionLevelId가 있음
 *
 * **사용 예시:**
 * ```tsx
 * // 생성 모드
 * <PositionLevelFormModal
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   onSuccess={handleSuccess}
 * />
 *
 * // 수정 모드
 * <PositionLevelFormModal
 *   isOpen={isModalOpen}
 *   positionLevelId="pos_manager"
 *   onClose={handleClose}
 *   onSuccess={handleSuccess}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { X, Loader } from 'lucide-react';
import { usePositionLevel } from '../../../shared/hooks/usePositionLevels';
import { usePositionLevelMutations } from '../../../shared/hooks/usePositionLevelMutations';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';

interface PositionLevelFormModalProps {
  /** 모달 열림 여부 */
  isOpen: boolean;

  /** 수정할 직위 ID (undefined이면 생성 모드) */
  positionLevelId?: string;

  /** 모달 닫기 콜백 */
  onClose: () => void;

  /** 저장 성공 시 콜백 */
  onSuccess: () => void;
}

/**
 * 직위 생성/수정 모달 폼 컴포넌트
 */
export function PositionLevelFormModal({
  isOpen,
  positionLevelId,
  onClose,
  onSuccess,
}: PositionLevelFormModalProps) {
  // 모드 판별: positionLevelId가 있으면 수정 모드
  const isEditMode = !!positionLevelId;

  // 기존 데이터 로드 (수정 모드)
  const { positionLevel, isLoading: isLoadingData } = usePositionLevel(positionLevelId);

  // Mutations Hook
  const { createPositionLevel, updatePositionLevel } = usePositionLevelMutations();
  const overlayProps = useModalOverlayClose(onClose);

  // 폼 상태
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    order: '',
    rankWeight: '',
    requiredRecognizedYearsFromHire: '',
    status: 'active' as 'active' | 'inactive',
    description: '',
  });

  // V50.1: 사원→대리 학력별 기준 (사원 직위일 때만 사용)
  const [staffToAssistantYears, setStaffToAssistantYears] = useState({
    highSchool: '6',
    college2: '4',
    college3: '3',
    university: '2',
  });

  // 사원 직위 여부 판단
  const isStaffPosition =
    positionLevelId === 'pos_staff' ||
    positionLevel?.code === 'STAFF' ||
    positionLevel?.name === '사원';

  // 제출 중 상태
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 에러 메시지
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 수정 모드에서 기존 데이터 로드
  useEffect(() => {
    if (isEditMode && positionLevel) {
      setFormData({
        name: positionLevel.name || '',
        code: positionLevel.code || '',
        order: positionLevel.order?.toString() || '',
        rankWeight: positionLevel.rankWeight?.toString() || '',
        requiredRecognizedYearsFromHire:
          positionLevel.requiredRecognizedYearsFromHire?.toString() || '',
        status: positionLevel.status || 'active',
        description: positionLevel.description || '',
      });

      // V50.1: 사원→대리 학력별 기준 로드
      const eduYears = positionLevel.promotionRule?.staffToAssistantYearsByEducation;
      if (eduYears) {
        setStaffToAssistantYears({
          highSchool: eduYears.highSchool?.toString() || '6',
          college2: eduYears.college2?.toString() || '4',
          college3: eduYears.college3?.toString() || '3',
          university: eduYears.university?.toString() || '2',
        });
      } else {
        // 기본값
        setStaffToAssistantYears({
          highSchool: '6',
          college2: '4',
          college3: '3',
          university: '2',
        });
      }
    }
  }, [isEditMode, positionLevel]);

  // 모달이 닫힐 때 폼 초기화
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        code: '',
        order: '',
        rankWeight: '',
        requiredRecognizedYearsFromHire: '',
        status: 'active',
        description: '',
      });
      // V50.1: 학력별 기준 초기화
      setStaffToAssistantYears({
        highSchool: '6',
        college2: '4',
        college3: '3',
        university: '2',
      });
      setErrorMessage(null);
    }
  }, [isOpen]);

  // 폼 입력 핸들러
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // V50.1: 학력별 기준 입력 핸들러
  const handleStaffYearsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    // 숫자만 허용 (빈값 또는 양수)
    if (value === '' || /^\d+$/.test(value)) {
      setStaffToAssistantYears(prev => ({ ...prev, [name]: value }));
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    // 필수 필드 검증
    if (!formData.name.trim()) {
      setErrorMessage('직위명을 입력해주세요.');
      return;
    }

    if (!formData.rankWeight.trim()) {
      setErrorMessage('위계 가중치를 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && positionLevelId) {
        // 수정 모드
        const updateData: any = {
          name: formData.name.trim(),
          code: formData.code.trim() || undefined,
          order: formData.order ? parseInt(formData.order) : undefined,
          rankWeight: parseInt(formData.rankWeight),
          requiredRecognizedYearsFromHire: formData.requiredRecognizedYearsFromHire
            ? parseInt(formData.requiredRecognizedYearsFromHire)
            : null,
          status: formData.status,
          description: formData.description.trim() || undefined,
        };

        // V50.1: 사원 직위일 때 학력별 기준 저장
        if (isStaffPosition) {
          updateData.promotionRule = {
            ...(positionLevel?.promotionRule || {}),
            staffToAssistantYearsByEducation: {
              highSchool: staffToAssistantYears.highSchool
                ? parseInt(staffToAssistantYears.highSchool)
                : 6,
              college2: staffToAssistantYears.college2
                ? parseInt(staffToAssistantYears.college2)
                : 4,
              college3: staffToAssistantYears.college3
                ? parseInt(staffToAssistantYears.college3)
                : 3,
              university: staffToAssistantYears.university
                ? parseInt(staffToAssistantYears.university)
                : 2,
            },
          };
        }

        await updatePositionLevel(positionLevelId, updateData);
      } else {
        // 생성 모드
        await createPositionLevel({
          name: formData.name.trim(),
          code: formData.code.trim() || undefined,
          order: formData.order ? parseInt(formData.order) : undefined,
          rankWeight: parseInt(formData.rankWeight),
          requiredRecognizedYearsFromHire: formData.requiredRecognizedYearsFromHire
            ? parseInt(formData.requiredRecognizedYearsFromHire)
            : null,
          status: formData.status,
          description: formData.description.trim() || undefined,
        });
      }

      setIsSubmitting(false);
      onSuccess(); // 성공 콜백 (목록 새로고침 등)
      onClose(); // 모달 닫기
    } catch (err: any) {
      console.error('직위 저장 실패:', err);
      setErrorMessage(err.message || '직위 저장에 실패했습니다.');
      setIsSubmitting(false);
    }
  };

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  // 로딩 중 (수정 모드에서 데이터 로드 중)
  if (isEditMode && isLoadingData) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '16px', color: 'var(--color-gray-600)' }}>
              직위 정보를 불러오는 중...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} {...overlayProps}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={styles.header}>
          <h2 style={styles.title}>{isEditMode ? '직위 수정' : '직위 추가'}</h2>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* 에러 메시지 */}
        {errorMessage && (
          <div style={styles.errorBox}>
            <p style={styles.errorText}>{errorMessage}</p>
          </div>
        )}

        {/* 폼 */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* 직위명 (필수) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              직위명 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="예: 과장"
              style={styles.input}
              required
            />
          </div>

          {/* 시스템 코드 (선택) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>시스템 코드</label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="예: MANAGER (영문 대문자)"
              style={styles.input}
            />
            <p style={styles.helpText}>영문 대문자로 입력하세요 (선택)</p>
          </div>

          {/* 위계 가중치 (필수) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              위계 가중치 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="number"
              name="rankWeight"
              value={formData.rankWeight}
              onChange={handleInputChange}
              placeholder="예: 3"
              style={styles.input}
              min="1"
              required
            />
            <p style={styles.helpText}>
              숫자가 클수록 높은 직위 (예: 사원=1, 대리=2, 과장=3, ...)
            </p>
          </div>

          {/* 정렬 순서 (선택) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>정렬 순서</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleInputChange}
              placeholder="미입력 시 자동 계산"
              style={styles.input}
              min="1"
            />
            <p style={styles.helpText}>화면 표시 순서 (작은 값부터 표시, 미입력 시 자동 계산)</p>
          </div>

          {/* 승진 기준 인정 근무연한 (선택) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>승진 기준 인정 근무연한 (년)</label>
            <input
              type="number"
              name="requiredRecognizedYearsFromHire"
              value={formData.requiredRecognizedYearsFromHire}
              onChange={handleInputChange}
              placeholder="예: 9 (9년 이상)"
              style={styles.input}
              min="0"
            />
            <p style={styles.helpText}>
              해당 직위 획득에 필요한 인정 근무연한 (정규직 입사일 + 학력 연한)
              <br />
              비워두면 근무연한 기준 없음 (임명직)
            </p>
          </div>

          {/* 사용 여부 (선택) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>사용 여부</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              style={styles.select}
            >
              <option value="active">사용</option>
              <option value="inactive">미사용</option>
            </select>
          </div>

          {/* 설명 (선택) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>설명</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="직위에 대한 설명 (선택)"
              style={styles.textarea}
              rows={3}
            />
          </div>

          {/* V50.1: 사원→대리 학력별 진급 기준 (사원 직위일 때만 표시) */}
          {isEditMode && isStaffPosition && (
            <div style={styles.staffYearsSection}>
              <div style={styles.sectionHeader}>
                <span style={styles.sectionTitle}>사원 → 대리 진급 기준 (학력별)</span>
                <span style={styles.sectionBadge}>V50</span>
              </div>
              <p style={styles.sectionHelpText}>
                사원에서 대리로 진급하기 위해 필요한 연차를 학력별로 설정합니다.
              </p>
              <div style={styles.staffYearsGrid}>
                <div style={styles.staffYearsItem}>
                  <label style={styles.label}>고졸 (년)</label>
                  <input
                    type="number"
                    name="highSchool"
                    value={staffToAssistantYears.highSchool}
                    onChange={handleStaffYearsChange}
                    placeholder="6"
                    style={styles.input}
                    min="0"
                  />
                </div>
                <div style={styles.staffYearsItem}>
                  <label style={styles.label}>초대졸 2년 (년)</label>
                  <input
                    type="number"
                    name="college2"
                    value={staffToAssistantYears.college2}
                    onChange={handleStaffYearsChange}
                    placeholder="4"
                    style={styles.input}
                    min="0"
                  />
                </div>
                <div style={styles.staffYearsItem}>
                  <label style={styles.label}>초대졸 3년 (년)</label>
                  <input
                    type="number"
                    name="college3"
                    value={staffToAssistantYears.college3}
                    onChange={handleStaffYearsChange}
                    placeholder="3"
                    style={styles.input}
                    min="0"
                  />
                </div>
                <div style={styles.staffYearsItem}>
                  <label style={styles.label}>대졸 4년 (년)</label>
                  <input
                    type="number"
                    name="university"
                    value={staffToAssistantYears.university}
                    onChange={handleStaffYearsChange}
                    placeholder="2"
                    style={styles.input}
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 버튼 */}
          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              취소
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>{isEditMode ? '수정 중...' : '생성 중...'}</span>
                </>
              ) : (
                <span>{isEditMode ? '수정' : '생성'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'var(--color-gray-500)',
    transition: 'color 0.2s',
  },
  errorBox: {
    margin: '16px 24px 0',
    padding: '12px 16px',
    backgroundColor: '#fee',
    borderRadius: '8px',
    border: '1px solid var(--color-error)',
  },
  errorText: {
    color: 'var(--color-error)',
    fontSize: '14px',
    margin: 0,
  },
  form: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  helpText: {
    fontSize: '12px',
    color: 'var(--color-gray-500)',
    marginTop: '4px',
    margin: 0,
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid var(--color-gray-200)',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // V50.1: 사원→대리 학력별 기준 섹션 스타일
  staffYearsSection: {
    marginTop: '24px',
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid var(--color-gray-200)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
  },
  sectionBadge: {
    padding: '2px 8px',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    fontSize: '10px',
    fontWeight: '600',
    borderRadius: '4px',
  },
  sectionHelpText: {
    fontSize: '12px',
    color: 'var(--color-gray-500)',
    marginTop: 0,
    marginBottom: 16,
  },
  staffYearsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  staffYearsItem: {
    // 개별 아이템 스타일 (label, input은 기존 스타일 사용)
  },
};
